import React, { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';

function takeHomeError(error) {
  if (error?.status === 409 && error?.code === 'revision_conflict') {
    return 'Your beat changed while we were saving. Tap Take it home again.';
  }
  if (error?.status === 409 && error?.code === 'takehome_busy') {
    return 'Your beat is already being prepared. Wait a moment, then try again.';
  }
  if (error?.status === 429 || error?.code === 'slow_down') {
    const seconds = Number(error?.retry_after_seconds);
    return Number.isFinite(seconds) && seconds > 0
      ? `Too many tries. Wait ${seconds} seconds, then try again.`
      : 'Too many tries. Wait a little, then try again.';
  }
  if (error?.status === 410 || error?.code === 'program_ended') {
    return 'This class has ended. Ask your teacher for help taking your beat home.';
  }
  if (error?.status === 404 || error?.code === 'not_created') {
    return 'You have not made a take-home QR for this beat yet.';
  }
  return 'We could not make your take-home QR. Check your connection and try again.';
}

function shortUrl(value) {
  try {
    const url = new URL(value);
    return `${url.host}${url.pathname}`;
  } catch {
    return value;
  }
}

export function TakeHomeModal({ open, slot, classCode, onClose, onCreate, onShow, onDownload }) {
  const [status, setStatus] = useState('idle');
  const [result, setResult] = useState(null);
  const [message, setMessage] = useState('');
  const [qrSource, setQrSource] = useState('');
  const [downloading, setDownloading] = useState(false);

  // Opening the panel saves the beat for home right away, so it reaches the at-home view and the
  // showcase playlist even if the kid never taps Download (most kids won't have a phone).
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setResult(null); setMessage(''); setQrSource(''); setStatus('saving');
    Promise.resolve().then(() => onCreate()).then((next) => {
      if (active) { setResult(next); setStatus('ready'); }
    }).catch((error) => {
      if (active) { setStatus('error'); setMessage(takeHomeError(error)); }
    });
    return () => { active = false; };
    // Save once per open or beat switch; onCreate is recreated by every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, slot]);

  useEffect(() => {
    if (!result?.take_home_url) return undefined;
    let active = true;
    QRCode.toDataURL(result.take_home_url, { width: 300, margin: 1, errorCorrectionLevel: 'M' })
      .then((source) => { if (active) setQrSource(source); })
      .catch(() => { if (active) setMessage('The QR could not be drawn. Use the link below.'); });
    return () => { active = false; };
  }, [result]);

  const expires = useMemo(() => {
    if (!result?.expires_at) return '';
    const value = new Date(result.expires_at);
    return Number.isNaN(value.getTime()) ? '' : value.toLocaleDateString();
  }, [result]);

  if (!open) return null;
  const retry = async () => {
    setStatus('saving'); setMessage('');
    try { setResult(await onCreate()); setStatus('ready'); } catch (error) { setStatus('error'); setMessage(takeHomeError(error)); }
  };
  const download = async () => {
    setDownloading(true);
    try { await onDownload?.(); } catch (error) { setMessage('We could not make your MP3. Try again.'); } finally { setDownloading(false); }
  };

  return <div className="takehome-modal-backdrop" role="presentation">
    <section className="takehome-modal" role="dialog" aria-modal="true" aria-labelledby="takehome-title">
      <button className="takehome-close" aria-label="Close take-home panel" onClick={onClose}>Close</button>
      <img className="takehome-pizzul" src="/Pizzul_Hero.png" alt="" />
      <h2 id="takehome-title">Take Beat {slot} home</h2>
      <p className="takehome-save-status" data-testid="takehome-save-status" role="status">
        {status === 'saving' ? 'Saving your beat for home...' : status === 'ready' ? 'Saved for home.' : status === 'error' ? 'Not saved for home yet.' : ''}
        {status === 'error' && <button className="takehome-secondary" onClick={retry}>Try again</button>}
      </p>
      <button className="takehome-primary takehome-download" onClick={download} disabled={downloading}>
        {downloading ? 'Making your MP3...' : 'Download MP3'}
      </button>
      <p className="takehome-listen-home"><strong>Listen at home:</strong> go to arranger.letthebeatbuild.me/join, type your class code {classCode}, tap your name and your pictures.</p>
      <details className="takehome-phone">
        <summary>Have a phone? Scan to save it</summary>
        {result ? <div className="takehome-phone-body">
          {qrSource ? <img className="takehome-qr" src={qrSource} alt={`QR code for Beat ${slot} take-home link`} />
            : <div className="takehome-qr takehome-qr-loading" aria-label="Making QR code" />}
          <a className="takehome-short-url" href={result.take_home_url} target="_blank" rel="noreferrer">{shortUrl(result.take_home_url)}</a>
          {expires && <p className="takehome-expiry">Available until {expires}</p>}
        </div> : <p className="takehome-expiry">{status === 'saving' ? 'Your QR will show up once your beat is saved.' : 'Save your beat first, then your QR shows up here.'}</p>}
      </details>
      {message && <p className="takehome-message" role="alert">{message}</p>}
    </section>
  </div>;
}

export default TakeHomeModal;
